/** Reviews (S40), a review (S41), Ask for a review (S42) and requests sent (S44). docs/50 §4.4, docs/52 §3.5. */
import { Banner, Button, Card, Chip, EmptyState, Filters, Icon, List, Row, TextArea, TextField, type Tone } from '@ff/app-ui';
import type { reviews as R } from '@ff/contracts';
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import type { z } from 'zod';
import { errorText, idemKey, ownWords } from '../lib/api.ts';
import { useAttest } from '../lib/attest.ts';
import { invalidate, send, setCached, useQuery, useSend } from '../lib/data.ts';
import { rowTime, shortDate, timeLabel } from '../lib/format.ts';
import { location, navigate, scoped } from '../lib/router.ts';
import { can, useReady } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { haptic, toast, tz } from '../lib/ui.ts';
import { S } from '@ff/app-ui/copy';
import { Info, Loaded, LoadError, PrimaryAction, Screen, Section, Stars } from '../ui/common.tsx';
import { Split } from '../ui/split.tsx';
import { AttestField, MoreWays, announceAsk, emailError, useFocusInvalid, useReviewLink } from './asks.tsx';

type Filter = 'all' | 'needs_you' | 'low';
type ReplyState = z.infer<typeof R.ReviewSummary>['replyState'];

const REPLY_TONE: Record<ReplyState, Tone> = { none: 'muted', drafted: 'neutral', awaiting_client: 'warn', approved: 'neutral', posted: 'ok', not_replying: 'muted' };

export function ReviewsScreen({ id }: { id?: string }): JSX.Element {
  return <Split list={<ReviewList selected={id} />} detail={id ? <ReviewDetail id={id} /> : null} />;
}

function ReviewList({ selected }: { selected?: string }): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const zone = useStore(tz);
  const [filter, setFilter] = useState<Filter>('all');
  const q = useQuery('reviews', { query: { filter, ...(loc.scope ? { location: loc.scope } : {}) } });
  const canAsk = can(s.membership, 'review_requests.create');
  return (
    <Screen title={S.reviews.title} query={q}>
      <Loaded q={q} skeleton="rows">
        {(d) => (
          <div class="stack" data-gap="l">
            {/* Email only (deck rulings c022, c072, c100): no texting status, whatever a messaging profile says. */}
            <p class="t-small t-muted cluster">
              <Icon name="mail" size={16} /> {S.reviews.emailOnly}
              {can(s.membership, 'review_templates.edit') && <a class="section-link" href="/reviews/templates">{S.reviews.templates.link}</a>}
            </p>
            <Card>
              <div class="card-head">
                <h2 class="t-heading">{S.reviews.paceTitle}</h2>
                <Info title={S.reviews.paceInfo[0]} lines={S.reviews.paceInfo.slice(1)} />
              </div>
              <p>{S.reviews.pace(d.pace.thisWeek, d.pace.topThreeWeeklyAverage)}</p>
            </Card>
            <Filters label={S.reviews.filters} value={filter} onChange={setFilter} options={(['all', 'needs_you', 'low'] as Filter[]).map((f) => ({ value: f, label: S.reviews.filter[f]!, count: f === 'needs_you' ? d.items.filter((r) => r.needsYou).length || null : null }))} />
            {d.items.length ? (
              <List label={S.reviews.title}>
                {d.items.map((r) => (
                  <li key={r.id}>
                    <Row
                      href={scoped(`/reviews/${r.id}`, loc.scope)}
                      current={selected === r.id}
                      unread={r.needsYou}
                      unreadLabel={S.reviews.reply.awaiting_client}
                      title={
                        <span class="review-row-title">
                          <Stars rating={r.rating} label={S.reviews.stars(r.rating)} />
                          <span>{r.authorDisplay ?? ''}</span>
                        </span>
                      }
                      meta={r.comment ? <span class="clamp-2">{r.comment}</span> : S.reviews.noComment}
                      time={rowTime(r.createdTime, zone)}
                      chip={<Chip tone={REPLY_TONE[r.replyState]}>{S.reviews.reply[r.replyState]}</Chip>}
                    />
                  </li>
                ))}
              </List>
            ) : (
              <EmptyState title={filter === 'all' ? S.reviews.firstRunTitle : S.reviews.empty} body={filter === 'all' ? S.reviews.firstRun : undefined} action={canAsk ? <Button variant="secondary" href="/reviews/ask">{S.reviews.ask}</Button> : null} />
            )}
            <div class="cluster">
              <a class="section-link" href={scoped('/reviews/sent', loc.scope)}>{S.reviews.sent}</a>
              {canAsk && <a class="section-link" href={scoped('/reviews/card', loc.scope)}>{S.reviews.qr}</a>}
            </div>
          </div>
        )}
      </Loaded>
      {canAsk && <PrimaryAction href={scoped('/reviews/ask', loc.scope)} icon="star">{S.reviews.ask}</PrimaryAction>}
    </Screen>
  );
}

function ReviewDetail({ id }: { id: string }): JSX.Element {
  const loc = useStore(location);
  const zone = useStore(tz);
  const q = useQuery('review', { params: { id } });
  const act = useSend();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  if (q.error && !q.data) return <div class="screen"><LoadError error={q.error} onRetry={q.refetch} /></div>;
  const r = q.data;
  const reply = async (body: z.input<typeof R.ReviewReplyBody>, msg: string) => {
    const out = await act.run(() => send('reviewReply', { params: { id }, body }));
    if (!out) return;
    haptic();
    setCached('review', { params: { id } }, () => out);
    invalidate('reviews', 'home');
    setEditing(false);
    toast(msg);
  };
  return (
    <Screen title={r ? `${S.reviews.stars(r.rating)}${r.authorDisplay ? `, ${r.authorDisplay}` : ''}` : S.reviews.title} back={scoped('/reviews', loc.scope)} query={q}>
      <Loaded q={q} skeleton="detail">
        {(r) => (
          <div class="stack" data-gap="l">
            <Card>
              <div class="stack" data-gap="s">
                <Stars rating={r.rating} label={S.reviews.stars(r.rating)} />
                {r.comment ? <blockquote class="lead-message">{r.comment}</blockquote> : <p class="t-muted">{S.reviews.noComment}</p>}
                <p class="t-small t-muted">{shortDate(r.createdTime, zone)}</p>
                {r.googleUrl && (
                  <Button href={r.googleUrl} external variant="quiet" iconEnd="external">
                    {S.reviews.seeOnGoogle}
                  </Button>
                )}
              </div>
            </Card>
            {r.reply && (
              <Section title={r.reply.state === 'posted' ? S.reviews.yourReply : S.reviews.ourDraft} id="reply">
                <Card>
                  {editing ? (
                    <TextArea label={S.reviews.editLabel} value={text} onInput={setText} maxLength={4096} rows={6} error={act.error ? errorText(act.error, S.errors.offlineSend) : null} />
                  ) : (
                    <p>{r.reply.text}</p>
                  )}
                  <p class="t-small t-muted" style="margin-top:12px">
                    {r.reply.state === 'posted' && r.reply.postedAt ? `${S.reviews.reply.posted} ${shortDate(r.reply.postedAt, zone)}` : S.reviews.draftBy[r.reply.draftedBy]}
                  </p>
                </Card>
                {r.sensitive && r.reply.state !== 'posted' && <p class="t-small t-muted">{S.reviews.sensitiveNote}</p>}
                {r.canPost && r.reply.state !== 'posted' && r.reply.state !== 'approved' && r.reply.state !== 'not_replying' && (
                  <div class="stack" data-gap="s" style="margin-top:16px">
                    {editing ? (
                      <>
                        <Button variant="primary" block busy={act.busy} onClick={() => void reply({ action: 'edit', text: text.trim() }, S.reviews.queued)}>
                          {S.reviews.saveReply}
                        </Button>
                        <Button variant="quiet" block onClick={() => setEditing(false)}>
                          {S.app.cancel}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="primary" block busy={act.busy} icon="send" onClick={() => void reply({ action: 'post' }, S.reviews.queued)}>
                          {S.reviews.postIt}
                        </Button>
                        <div class="btn-row" data-equal>
                          <Button
                            icon="edit"
                            onClick={() => {
                              setText(r.reply!.text);
                              setEditing(true);
                            }}
                          >
                            {S.reviews.changeIt}
                          </Button>
                          <Button icon="phone" onClick={() => void reply({ action: 'call_me' }, S.reviews.callBooked)}>
                            {S.reviews.callMe}
                          </Button>
                        </div>
                        <Button variant="quiet" block onClick={() => void reply({ action: 'not_replying' }, S.reviews.reply.not_replying!)}>
                          {S.reviews.notReplying}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </Section>
            )}
          </div>
        )}
      </Loaded>
    </Screen>
  );
}

/** S42. A route rather than a sheet so "Job done" on a lead can deep-link into it prefilled. */
export function AskScreen(): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const zone = useStore(tz);
  const prefill = (() => {
    try {
      const v = JSON.parse(sessionStorage.getItem('ff-rr-prefill') ?? 'null') as { leadId?: string; firstName?: string | null; phone?: string | null; email?: string | null } | null;
      return v;
    } catch {
      return null;
    }
  })();
  // The first name is optional (c133): an ask needs only an email. Without one the email opens "Hi there".
  const [first, setFirst] = useState(prefill?.firstName ?? '');
  // Email only (c022, c072, c100): a lead's phone is never used for a review ask.
  const [contact, setContact] = useState(prefill?.email ?? '');
  const attest = useAttest(s.me.user.id);
  const [errors, setErrors] = useState<{ contact?: string; attest?: string }>({});
  const [key] = useState(idemKey);
  const act = useSend();
  const { ref, focusInvalid } = useFocusInvalid();

  const submit = async (e: Event) => {
    e.preventDefault();
    const errs: { contact?: string; attest?: string } = {};
    const c = contact.trim();
    const bad = emailError(c);
    if (bad) errs.contact = bad;
    if (!attest.ok) errs.attest = S.reviews.attestError;
    setErrors(errs);
    if (errs.contact || errs.attest) return focusInvalid();
    attest.remember();
    const out = await act.run(() =>
      send('reviewRequestCreate', {
        body: {
          ...(first.trim() ? { firstName: first.trim() } : {}),
          email: c,
          entry: 'form',
          ...(prefill?.leadId ? { leadId: prefill.leadId } : {}),
          ...(loc.scope ? { locationId: loc.scope } : {}),
          attestation: attest.body,
          idempotencyKey: key,
        },
      }),
    );
    // On an error what they typed stays put and the same key goes with the retry.
    if (!out) return;
    sessionStorage.removeItem('ff-rr-prefill');
    invalidate('reviewRequests', 'home', 'reviewLink');
    // Scheduled with an Undo, or a plain line when nothing was sent (already asked, or they opted out).
    announceAsk(out, zone);
    navigate(scoped('/reviews/sent', loc.scope), { replace: true });
  };

  return (
    <Screen title={S.reviews.askTitle} back={scoped('/reviews', loc.scope)}>
      <div class="stack form-narrow" data-gap="l">
        <form ref={ref} class="stack" onSubmit={submit} noValidate>
          {s.membership.role === 'crew' && <p class="t-muted">{S.reviews.crewNote}</p>}
          <TextField label={S.reviews.firstName} autoComplete="off" autoCapitalize="words" value={first} onInput={setFirst} maxLength={60} />
          <TextField label={S.reviews.contact} hint={S.reviews.contactHint} type="email" inputMode="email" autoComplete="off" autoCapitalize="none" spellcheck={false} value={contact} onInput={setContact} error={errors.contact ?? null} maxLength={254} />
          <AttestField attest={attest} error={errors.attest} />
          <p class="t-small t-muted">{S.reviews.when}</p>
          {act.error && <Banner tone="danger" role="alert" title={ownWords(act.error, S.errors.offlineSend)} />}
          <Button type="submit" variant="primary" size="lg" block busy={act.busy} icon="send">
            {S.reviews.send}
          </Button>
        </form>
        <MoreWays />
      </div>
    </Screen>
  );
}

const RR_TONE: Record<string, Tone> = { scheduled: 'warn', held: 'warn', sent: 'neutral', opened: 'ok', clicked: 'ok', failed: 'danger', cancelled: 'muted', suppressed: 'muted', duplicate: 'muted' };

export function SentScreen(): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const zone = useStore(tz);
  const q = useQuery('reviewRequests', { query: loc.scope ? { location: loc.scope } : {} });
  const canAsk = can(s.membership, 'review_requests.create');
  // Walk ups: taps on the card and on the link the owner shared themselves, which no row below accounts for.
  const link = useReviewLink(canAsk).data;
  const walkUps = link?.ready ? link.taps.last7Days.card + link.taps.last7Days.link : null;
  return (
    <Screen title={S.reviews.sentTitle} back={scoped('/reviews', loc.scope)} query={q}>
      <Loaded q={q}>
        {(d) => (
          <div class="stack" data-gap="m">
            {walkUps !== null && (
              <p class="t-small t-muted cluster">
                <Icon name="qr" size={16} /> {S.reviews.card.walkUps(walkUps)}
                <a class="section-link" href={scoped('/reviews/card', loc.scope)}>{S.reviews.qr}</a>
              </p>
            )}
            {d.items.length ? (
              <List>
                {d.items.map((r) => {
                  const when = r.firstAt ?? r.scheduledFor ?? r.createdAt;
                  // A name is optional (c133), so a row without one shows its masked email. Opted out wins over purged.
                  const title = r.firstName ?? r.contactLabel ?? S.reviews.purgedTitle;
                  const meta = r.optedOut
                    ? S.reviews.optedOut
                    : r.contactPurged
                      ? S.reviews.purged
                      : [r.firstName ? r.contactLabel : null, r.firstChannel ? S.leads.channel[r.firstChannel] : null, r.reminderAt ? S.reviews.reminderAt(shortDate(r.reminderAt, zone)) : null].filter(Boolean).join(' · ');
                  return (
                    <li key={r.id}>
                      <Row
                        title={title}
                        meta={meta}
                        time={r.status === 'scheduled' || r.status === 'held' ? timeLabel(when, zone) : rowTime(when, zone)}
                        chip={<Chip tone={RR_TONE[r.status]}>{S.reviews.rrStatus[r.status]}</Chip>}
                      />
                    </li>
                  );
                })}
              </List>
            ) : (
              <EmptyState title={S.reviews.sentTitle} body={S.reviews.sentEmpty} action={canAsk ? <Button variant="primary" href={scoped('/reviews/ask', loc.scope)}>{S.reviews.ask}</Button> : null} />
            )}
          </div>
        )}
      </Loaded>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ review ask emails (c131)

type Template = z.infer<typeof R.ReviewTemplate>;

/** The owner's review ask emails: written for them, theirs to change, voice checked by the server. */
export function ReviewTemplatesScreen(): JSX.Element {
  const s = useReady();
  const q = useQuery('reviewTemplates', {}, { enabled: can(s.membership, 'review_templates.edit') });
  if (!can(s.membership, 'review_templates.edit'))
    return (
      <Screen title={S.reviews.templates.title} back="/reviews">
        <EmptyState title={S.reviews.templates.title} body={S.reviews.templates.onlyOwner} />
      </Screen>
    );
  return (
    <Screen title={S.reviews.templates.title} back="/reviews" lead={S.reviews.templates.lead} query={q}>
      <Loaded q={q} skeleton="cards">
        {(d) => (
          <div class="stack form-narrow" data-gap="l">
            <p class="t-small t-muted">{S.reviews.templates.fixed}</p>
            {d.items.map((item) => (
              <TemplateEditor key={`${item.kind}-${item.updatedAt ?? 'ours'}`} item={item} />
            ))}
          </div>
        )}
      </Loaded>
    </Screen>
  );
}

function TemplateEditor({ item }: { item: Template }): JSX.Element {
  const [subject, setSubject] = useState(item.subject);
  const [body, setBody] = useState(item.body);
  const [local, setLocal] = useState<string | null>(null);
  const act = useSend();
  const dirty = subject !== item.subject || body !== item.body;
  const T = S.reviews.templates;
  const put = async (payload: z.input<typeof R.ReviewTemplateBody>, done: string) => {
    const out = await act.run(() => send('reviewTemplateUpdate', { body: payload }));
    if (!out) return;
    setCached('reviewTemplates', {}, () => out);
    haptic();
    toast(done);
  };
  return (
    <Section title={T[item.kind]} id={`tpl-${item.kind}`} aside={<Chip tone={item.edited ? 'ok' : 'outline'}>{item.edited ? T.edited : T.ours}</Chip>}>
      <Card>
        <form
          class="stack"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (subject.trim().length < 3 || body.trim().length < 10) return setLocal(T.emptyError);
            setLocal(null);
            void put({ action: 'save', kind: item.kind, subject: subject.trim(), body: body.trim() }, T.saved);
          }}
        >
          <TextField label={T.subject} value={subject} onInput={setSubject} maxLength={120} />
          <TextArea label={T.body} value={body} onInput={setBody} maxLength={1200} rows={7} error={local ?? (act.error ? ownWords(act.error, S.errors.offlineSend) : null)} />
          <p class="t-small t-muted">{T.placeholders}</p>
          <div class="btn-row">
            <Button type="submit" variant="primary" busy={act.busy} disabled={!dirty}>
              {T.save}
            </Button>
            {item.edited && (
              <Button variant="quiet" onClick={() => void put({ action: 'reset', kind: item.kind }, T.resetDone)}>
                {T.reset}
              </Button>
            )}
          </div>
        </form>
      </Card>
      <details class="tpl-preview">
        <summary>
          <Icon name="chevron-down" size={20} /> {T.preview}
        </summary>
        <p class="t-heading">{item.preview.subject}</p>
        <p class="tpl-text">{item.preview.text}</p>
      </details>
    </Section>
  );
}
